import { useMemo, useState, type ChangeEvent } from 'react';
import {
  Box,
  Chip,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  type TableRowProps,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

import type { ExchangeIssuedItem } from './types';
import { multiWordMatch } from '@/utils/multiWordMatch';
import {
  calculateGroupReplacementQty,
  formatIssuedDatesDisplay,
  groupExchangeIssuedItems,
} from '@/utils/iemIssuedItems';

export interface IssuedItemsPaneProps {
  items: ExchangeIssuedItem[];
  focusedIssuedItemId: string | null;
  onFocusItem: (issuedItemId: string) => void;
  pairedIssuedIds: Set<string>;
  replacementQtyByIssuedId: Record<string, number>;
  onUpdateReplacementQty: (issuedItemId: string, qty: number) => void;
}

const normalize = (value: string) => value.trim().toLowerCase();

interface IssuedItemGroupRow {
  groupId: string;
  lin: string;
  nsn: string;
  name: string;
  issuedAtDisplay: string;
  totalIssuedQty: number;
  replacementQty: number;
  items: ExchangeIssuedItem[];
  rowIssuedItemId: string;
  pairedCount: number;
  totalItems: number;
  isFocused: boolean;
  receiptTypes: Array<NonNullable<ExchangeIssuedItem['receiptType']>>;
  searchable: string;
}

const RECEIPT_TYPE_ORDER: Array<NonNullable<ExchangeIssuedItem['receiptType']>> = ['THR', 'PHR'];

const RECEIPT_TYPE_COLORS: Record<
  NonNullable<ExchangeIssuedItem['receiptType']>,
  'primary' | 'secondary'
> = {
  PHR: 'primary',
  THR: 'secondary',
};

export const IssuedItemsPane = ({
  items,
  focusedIssuedItemId,
  onFocusItem,
  pairedIssuedIds,
  replacementQtyByIssuedId,
  onUpdateReplacementQty,
}: IssuedItemsPaneProps) => {
  const [searchTerm, setSearchTerm] = useState('');

  const normalizedQuery = normalize(searchTerm);

  const groupedItems = useMemo<IssuedItemGroupRow[]>(() => {
    const rows = groupExchangeIssuedItems(items, { splitByReceiptType: true }).map((group) => {
      if (group.items.length === 0) {
        return null;
      }

      const pairedCount = group.items.filter((current) => pairedIssuedIds.has(current.id)).length;
      const representativeItem =
        group.items.find((current) => !pairedIssuedIds.has(current.id)) ?? group.items[0];

      if (!representativeItem) {
        return null;
      }

      const rowIssuedItemId = representativeItem.id;
      const receiptTypes = RECEIPT_TYPE_ORDER.filter((type) => group.receiptTypes.has(type));

      return {
        groupId: group.id,
        lin: group.lin,
        nsn: group.nsn,
        name: group.name,
        issuedAtDisplay: formatIssuedDatesDisplay(group.issuedDates),
        totalIssuedQty: group.totalIssuedQty,
        replacementQty: calculateGroupReplacementQty(group, replacementQtyByIssuedId),
        items: group.items,
        rowIssuedItemId,
        pairedCount,
        totalItems: group.items.length,
        isFocused: group.items.some((current) => current.id === focusedIssuedItemId),
        receiptTypes,
        searchable:
          `${group.name} ${group.lin} ${group.nsn} ${receiptTypes.join(' ')}`.toLowerCase(),
      } satisfies IssuedItemGroupRow;
    });

    return rows
      .filter((row): row is IssuedItemGroupRow => Boolean(row))
      .sort((a, b) => {
        const byName = a.name.localeCompare(b.name);
        if (byName !== 0) {
          return byName;
        }
        const byLin = a.lin.localeCompare(b.lin);
        if (byLin !== 0) {
          return byLin;
        }
        return a.nsn.localeCompare(b.nsn);
      });
  }, [items, focusedIssuedItemId, pairedIssuedIds, replacementQtyByIssuedId]);

  const filteredItems = useMemo(() => {
    if (!normalizedQuery) {
      return groupedItems;
    }

    return groupedItems.filter((group) => multiWordMatch(group.searchable, normalizedQuery));
  }, [groupedItems, normalizedQuery]);

  const handleRowSelect: TableRowProps['onClick'] = (event) => {
    const issuedItemId = event.currentTarget.getAttribute('data-issued-item-id');
    if (issuedItemId) {
      onFocusItem(issuedItemId);
    }
  };

  const handleRowKeyDown: TableRowProps['onKeyDown'] = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    const issuedItemId = event.currentTarget.getAttribute('data-issued-item-id');
    if (issuedItemId) {
      onFocusItem(issuedItemId);
    }
  };

  return (
    <Stack spacing={1.5} sx={{ height: '100%' }}>
      <TextField
        value={searchTerm}
        onChange={(event) => setSearchTerm(event.target.value)}
        placeholder="Search issued items"
        size="small"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
        }}
        fullWidth
      />

      <Paper variant="outlined" sx={{ flexGrow: 1, overflow: 'hidden' }}>
        {filteredItems.length === 0 ? (
          <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', p: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No issued items match this search.
            </Typography>
          </Stack>
        ) : (
          <TableContainer sx={{ maxHeight: 480 }}>
            <Table stickyHeader size="small" aria-label="Issued items">
              <TableHead>
                <TableRow>
                  <TableCell>LIN</TableCell>
                  <TableCell>NSN</TableCell>
                  <TableCell>Nomenclature</TableCell>
                  <TableCell>Receipt Type</TableCell>
                  <TableCell>Issued</TableCell>
                  <TableCell align="right">Replacement Qty</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredItems.map((group) => {
                  const issuedQty = Math.max(0, group.totalIssuedQty);
                  const allItemsPaired = group.pairedCount === group.totalItems;

                  const handleReplacementQtyChange = (event: ChangeEvent<HTMLInputElement>) => {
                    const inputValue = event.target.value.replace(/^0+/, '');
                    const raw = inputValue === '' ? 0 : Number(inputValue);
                    const sanitized = Number.isNaN(raw) ? 0 : raw;
                    const wholeNumber = Math.floor(sanitized);

                    if (wholeNumber > issuedQty) {
                      return;
                    }

                    const clamped = Math.max(0, wholeNumber);
                    let remaining = clamped;

                    group.items.forEach((item) => {
                      const itemMax = Math.max(0, item.issuedQty ?? 0);
                      const allocation = Math.min(itemMax, Math.max(0, remaining));
                      onUpdateReplacementQty(item.id, allocation);
                      remaining -= allocation;
                    });
                  };

                  return (
                    <TableRow
                      key={group.groupId}
                      data-issued-item-id={group.rowIssuedItemId}
                      hover
                      selected={group.isFocused}
                      tabIndex={0}
                      onClick={handleRowSelect}
                      onKeyDown={handleRowKeyDown}
                      sx={{
                        cursor: 'pointer',
                        '&.Mui-selected': (theme) => ({
                          backgroundColor: theme.palette.action.selected,
                        }),
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>{group.lin}</TableCell>
                      <TableCell>{group.nsn}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                          <Box
                            component="span"
                            sx={{ fontWeight: 500, flexGrow: 1, minWidth: 0, pr: 1 }}
                          >
                            {group.name}
                          </Box>
                          {group.pairedCount > 0 ? (
                            <Chip
                              label={
                                allItemsPaired
                                  ? 'Returned'
                                  : `${group.pairedCount}/${group.totalItems} in cart`
                              }
                              size="small"
                              color={allItemsPaired ? 'success' : 'primary'}
                              variant={allItemsPaired ? 'filled' : 'outlined'}
                              sx={{ ml: 'auto' }}
                            />
                          ) : null}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {group.receiptTypes.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        ) : (
                          <Stack
                            direction="row"
                            spacing={0.5}
                            sx={{ flexWrap: 'wrap', rowGap: 0.5 }}
                          >
                            {group.receiptTypes.map((receiptType) => (
                              <Chip
                                key={`${group.groupId}-${receiptType}`}
                                size="small"
                                label={receiptType}
                                color={RECEIPT_TYPE_COLORS[receiptType]}
                                variant="outlined"
                              />
                            ))}
                          </Stack>
                        )}
                      </TableCell>
                      <TableCell>{group.issuedAtDisplay}</TableCell>
                      <TableCell align="right">
                        <TextField
                          size="small"
                          value={group.replacementQty}
                          onChange={handleReplacementQtyChange}
                          disabled={issuedQty <= 0}
                          inputProps={{
                            inputMode: 'numeric',
                            pattern: '[0-9]*',
                            min: 0,
                            max: issuedQty,
                            style: { textAlign: 'right' },
                            'aria-label': `Replacement quantity for ${group.name}`,
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">/ {issuedQty}</InputAdornment>
                            ),
                          }}
                          sx={{ width: 120, ml: 'auto' }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Stack>
  );
};
